// FLAGS: authorization
// The route table, and the only place the CustomerSuccess gate is attached to a surface.
import {
  createBrowserRouter,
  Navigate,
  RouteObject,
  useParams,
} from "react-router";
import ProtectedRoute from "@src/auth/ProtectedRoute";
import PublicOnlyRoute from "@src/auth/PublicOnlyRoute";
import AppShell from "@src/layouts/AppShell";
import LoginPage from "@src/pages/login/LoginPage";
import EventsListPage from "@src/pages/EventsListPage";
import EventTypeSelectPage from "@src/pages/EventTypeSelectPage";
import EventNewPage from "@src/pages/EventNewPage";
import EventDetailPage from "@src/pages/EventDetailPage";
import EventEditPage from "@src/pages/EventEditPage";
import EventInviteesPage from "@src/pages/EventInviteesPage";
import EventAttendancePage from "@src/pages/EventAttendancePage";
import NotFoundPage from "@src/pages/NotFoundPage";

// FLAGS: authorization
// Wrapping is opt-in per route, so a scheduler surface added outside this helper is reachable
// signed-out. It hides UI only — the API policy on each endpoint is the boundary that withholds data.
/**
 * Remounts EventNewPage when the type slug changes. The page seeds its form state from the slug
 * once, in a useState initializer, while the heading and the survey field read the param live — so
 * a param change without a remount would leave them disagreeing, and a page reading "create a group
 * session" would submit `eventType: "masterclass"`, provisioning a Zoom webinar instead of a
 * meeting. No route today changes the slug in place; this makes that stay true for free.
 */
const EventNewRoute = () => {
  const { eventType = "" } = useParams();
  return <EventNewPage key={eventType} />;
};

export const protectedRoute = (route: RouteObject): RouteObject => ({
  element: <ProtectedRoute />,
  children: [route],
});

export const publicOnlyRoute = (route: RouteObject): RouteObject => ({
  element: <PublicOnlyRoute />,
  children: [route],
});

// FLAGS: authorization
// `/login` and the catch-all 404 sit outside protectedRoute deliberately; everything under the shell
// sits inside it. Lifting a path out of that children array removes its gate with no other visible
// change.
//
// `/invite/:token` used to sit out here too. It is gone rather than un-gated: the attendee-facing
// half of the invite flow lives in OpenUp.Web booking, and this app never builds an invitee URL —
// ShareableLink renders whatever the API returns.
export const routes: RouteObject[] = [
  publicOnlyRoute({ path: "/login", element: <LoginPage /> }),
  protectedRoute({
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/events" replace /> },
      {
        path: "events",
        children: [
          { index: true, element: <EventsListPage /> },
          {
            path: "new",
            children: [
              { index: true, element: <EventTypeSelectPage /> },
              { path: ":eventType", element: <EventNewRoute /> },
            ],
          },
          {
            // Wrap with a shared EventLayout once detail/invitees/attendance
            // get common chrome (event header, tabs).
            path: ":id",
            children: [
              { index: true, element: <EventDetailPage /> },
              { path: "edit", element: <EventEditPage /> },
              { path: "invitees", element: <EventInviteesPage /> },
              { path: "attendance", element: <EventAttendancePage /> },
            ],
          },
        ],
      },
    ],
  }),
  { path: "*", element: <NotFoundPage /> },
];

const router = createBrowserRouter(routes);

export default router;
